import React, { useEffect, useState } from 'react';
import { X, Calendar as CalendarIcon, Loader2, AlertTriangle, Plane, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sprint C — Riya Reschedule Preview Modal.
 *
 * Calls POST /api/riya/reschedule/avail with Flag=CHECKFARE to preview
 * alternative flights on a new date. Reschedule is NOT committed here —
 * the user/agent can see fare diff + reschedule fee, and from there
 * choose to proceed via the existing Cancel + Rebook flow.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   booking: { booking_id, id, riya_pnr, contact_phone, flight_data: {from,to} }
 */
const STEPS = { INPUT: 'input', LOADING: 'loading', RESULTS: 'results', ERROR: 'error' };

export default function RiyaRescheduleModal({ open, onClose, booking = {} }) {
  const [step, setStep] = useState(STEPS.INPUT);
  const [date, setDate] = useState(null); // Date object
  const [errMsg, setErrMsg] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(STEPS.INPUT);
    setDate(null);
    setErrMsg('');
    setResult(null);
  }, [open]);

  if (!open) return null;

  const apiBookingId = getApiBookingRouteId(booking) || booking.id || booking.booking_id;
  const displayBookingRef = getUserFacingBookingId(booking);

  const submit = async () => {
    if (!date) {
      setErrMsg('Please pick a new date');
      return;
    }
    const isoDate = format(date, 'yyyy-MM-dd');
    const yyyymmdd = isoDate.replace(/-/g, '');
    setStep(STEPS.LOADING);
    setErrMsg('');
    try {
      const resp = await axios.post(`${API}/riya/reschedule/avail`, {
        booking_id: apiBookingId,
        departure: booking?.flight_data?.from || '',
        arrival: booking?.flight_data?.to || '',
        flight_date_yyyymmdd: yyyymmdd,
        contact_no: booking.contact_phone || '',
      }, { withCredentials: true, validateStatus: () => true });
      const data = resp.data;
      if (resp.status < 200 || resp.status >= 300) {
        const detail = data?.detail;
        const rawMsg = typeof detail === 'string' ? detail : (detail?.message || data?.message || `Reschedule preview failed (${resp.status})`);
        const supplierDown = resp.status === 502
          || /Proxy Error|Login|TravelAPI\.svc|ReadTimeout|Bad Gateway/i.test(String(rawMsg));
        throw new Error(supplierDown
          ? 'Riya supplier is temporarily unreachable. Please retry in a few minutes.'
          : rawMsg);
      }
      setResult(data);
      setStep(STEPS.RESULTS);
    } catch (e) {
      setErrMsg(e.message || 'Network error');
      setStep(STEPS.ERROR);
    }
  };

  const flights = result?.available_flights || [];
  const route = `${booking?.flight_data?.from || '—'} → ${booking?.flight_data?.to || '—'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="riya-reschedule-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-800">
              Reschedule Booking
              <span className="ml-2 text-xs font-medium text-gray-500">(Riya)</span>
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="riya-reschedule-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === STEPS.INPUT && (
            <>
              <div className="text-sm text-gray-700 space-y-1">
                <p>Booking: <span className="font-mono">{displayBookingRef}</span></p>
                <p>Route: <span className="font-semibold">{route}</span></p>
                <p>PNR: <span className="font-mono">{booking.riya_pnr || booking.pnr || '—'}</span></p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-2">New travel date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center gap-2 hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="riya-reschedule-date"
                    >
                      <CalendarIcon className="w-4 h-4 text-indigo-600" />
                      <span className={date ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                        {date ? format(date, 'PPP') : 'Pick a date'}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      data-testid="riya-reschedule-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {errMsg && <p className="text-xs text-red-600">{errMsg}</p>}
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={submit}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                  data-testid="riya-reschedule-preview-btn"
                >
                  Preview Options
                </button>
              </div>
            </>
          )}

          {step === STEPS.LOADING && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">Checking alternative flights…</p>
            </div>
          )}

          {step === STEPS.RESULTS && (
            <>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-indigo-700">Reschedule Fee</p>
                    <p className="text-base font-bold text-gray-800">₹{Number(result?.reschedule_fee || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-indigo-700">Fare Difference</p>
                    <p className="text-base font-bold text-gray-800">₹{Number(result?.fare_difference || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-indigo-700">Total Payable</p>
                    <p className="text-base font-bold text-emerald-700">₹{Number(result?.total_payable || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-800">Available flights for {date ? format(date, 'PPP') : ''}</p>
                {flights.length === 0 && (
                  <p className="text-sm text-gray-500 py-4 text-center">No alternative flights returned.</p>
                )}
                {flights.map((f, i) => (
                  <div key={f.FlightID || i} className="border border-gray-200 rounded-lg p-3 hover:border-indigo-300 transition" data-testid={`riya-reschedule-flight-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plane className="w-4 h-4 text-indigo-600" />
                        <span className="font-bold text-sm text-gray-800">{f.FlightNumber}</span>
                      </div>
                      <span className="text-sm font-bold text-emerald-700">+₹{Number(f.FareDifference || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2 flex items-center gap-2">
                      <span className="font-mono">{f.Origin}</span>
                      <span>{f.DepartureDateTime}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span>{f.ArrivalDateTime}</span>
                      <span className="font-mono">{f.Destination}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Baggage: {f.Baggage || '—'} · Seats: {f.AvailSeat || '—'}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  This is a preview. To proceed with the date change, please cancel the current booking
                  (penalty may apply) and book the new flight separately.
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium"
                data-testid="riya-reschedule-results-close"
              >
                Close
              </button>
            </>
          )}

          {step === STEPS.ERROR && (
            <div className="text-center py-4">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-base font-semibold text-gray-800">Could not load reschedule options</p>
              <p className="text-sm text-gray-600 mt-2">{errMsg}</p>
              <button onClick={onClose} className="mt-4 w-full px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
